import {
    useAddBucketCompanyCodeMapping,
    useBucketCompanyCodeMapping,
    useDeleteBucketCompanyCodeMapping
} from "./queries";
import {Button} from "reactstrap";
import {FaPlus} from "react-icons/fa";
import {BucketCompanyCodeForm, BucketCompanyCodeFormModal, BucketCompanyCodeTable} from "./components";
import {useBoolean} from "usehooks-ts";
import {extractAccessToken} from "../../helpers/jwt";
import Swal from "sweetalert2";
import {useQueryClient} from "@tanstack/react-query";
import axios, {AxiosError} from "axios";
import {BucketCompanyCode} from "../../models/config/BucketCompanyCode";

const getErrorMessage = (error: unknown) =>
    axios.isAxiosError(error)
        ? (error as AxiosError<{ message?: string }>).response?.data.message
        : error instanceof Error
            ? error.message
            : 'Unknown error occurred.';

export const DMSBucketCompanyCodeConfig = () => {
    const { data: bucketCompanyCodes, isFetching: isFetchingBucketCompanyCodes } = useBucketCompanyCodeMapping();
    
    const queryClient = useQueryClient();

    const { value: isOpenCreateModal, toggle: toggleIsOpenCreateModal } =
        useBoolean();
    const { mutate: addBucketCompanyCodeMapping } = useAddBucketCompanyCodeMapping();
    
    const { mutate: deleteBucketCompanyCodeMapping } = useDeleteBucketCompanyCodeMapping();
    
    const handleSubmitAddBucketCompanyCodeMapping = (bucketCompanyCode: BucketCompanyCode) => {
        const request = {
            ...bucketCompanyCode,
            created_by:
                extractAccessToken().citizen_id || extractAccessToken().username
        }

        addBucketCompanyCodeMapping(request, {
            onSuccess: async () => {
                await Swal.fire({
                    title: 'Success!',
                    icon: 'success'
                });
            },
            onError: async error => {
                await Swal.fire({
                    title: 'Error!',
                    icon: 'error',
                    text: getErrorMessage(error),
                });
            },
            onSettled: () => {
                toggleIsOpenCreateModal();

                queryClient.invalidateQueries({
                    queryKey: ['bucket-company-code-mapping']
                });
            }
        });
    };

    const handleDeleteBucketCompanyCodeMapping = async (bucketCompanyCode: BucketCompanyCode) => {
        const result = await Swal.fire({
            title: `ยืนยันการลบ ${bucketCompanyCode?.company_code}?`,
            icon: 'warning',
            showDenyButton: true,
            denyButtonText: 'ยกเลิก',
            confirmButtonText: 'ยืนยัน'
        });
        if (!result.isConfirmed) {
            return;
        }
        
        deleteBucketCompanyCodeMapping(bucketCompanyCode, {
            onSuccess: async () => {
                await Swal.fire({
                    title: 'Success!',
                    icon: 'success'
                });
            },
            onError: async error => {
                await Swal.fire({
                    title: 'Error!',
                    icon: 'error',
                    text: getErrorMessage(error),
                });
            },
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['bucket-company-code-mapping']
                });
            }
        });
    };
    
    return (
        <div className="master-data-content">
            <div className="tw-flex tw-justify-between">
                <div className="trapezoid-head tw-w-[200px]">
                    <span>DMS Bucket Company Code Config</span>
                </div>

                <Button
                    size="sm"
                    color="primary"
                    onClick={toggleIsOpenCreateModal}
                    className="tw-flex tw-items-center tw-gap-1 tw-self-center"
                >
                    <FaPlus />
                    <span>Create</span>
                </Button>
            </div>

            <div className="mat-box">
                <BucketCompanyCodeTable
                    isPending={isFetchingBucketCompanyCodes}
                    bucketCompanyCodes={bucketCompanyCodes}
                    onDelete={handleDeleteBucketCompanyCodeMapping}
                />
                
                <BucketCompanyCodeFormModal
                    title="Create Bucket Company Code Config"
                    isOpen={isOpenCreateModal}
                    onClose={toggleIsOpenCreateModal}
                    onSubmit={handleSubmitAddBucketCompanyCodeMapping}
                />
            </div>
        </div>
    );
};